import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function HotelsPage() {
  const [destinationQuery, setDestinationQuery] = useState('');
  const [selectedCityId, setSelectedCityId] = useState<string | number>('');
  const [destinations, setDestinations] = useState<any[]>([]);
  const [showDestinations, setShowDestinations] = useState(false);
  
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [guests, setGuests] = useState({ adults: 2, children: 0, rooms: 1 });
  const [showGuests, setShowGuests] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState('');

  const destRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (destRef.current && !destRef.current.contains(event.target as Node)) {
        setShowDestinations(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search for destinations
  useEffect(() => {
    const fetchDestinations = async () => {
      if (destinationQuery.length > 2) {
        try {
          const res = await axios.post('/api/travel/hotel-destinations', { text: destinationQuery });
          setDestinations(res.data.results || []);
          setShowDestinations(true);
        } catch (err) {
          console.error(err);
        }
      } else {
        setDestinations([]);
        setShowDestinations(false);
      }
    };
    
    // Only search if user typed, not if they selected from list
    if (!selectedCityId) {
      const timeoutId = setTimeout(fetchDestinations, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [destinationQuery, selectedCityId]);

  const handleSelectDestination = (dest: any) => {
    setDestinationQuery(dest.Name || dest.city_name || dest.name || '');
    setSelectedCityId(dest.Id || dest.city_id || dest.id || destinationQuery);
    setShowDestinations(false);
  };

  const handleSearch = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!selectedCityId || !checkIn || !checkOut) {
      setError('Please select a valid destination, check-in, and check-out date.');
      return;
    }

    setLoading(true);
    setError('');
    setHasSearched(true);
    setSearchResults([]);

    try {
      const response = await axios.post('/api/travel/search-hotels', {
        destination: selectedCityId,
        checkIn,
        checkOut,
        guests: guests.adults
      });
      
      if (response.data && response.data.results) {
        setSearchResults(response.data.results);
      } else {
        setSearchResults([]);
      }
    } catch (err: any) {
      console.error(err);
      setError('Could not find hotels. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col font-sans">
      <Header />
      
      {/* Premium Hero Section */}
      <section 
        className="relative pt-32 pb-24 bg-navy-900 bg-cover bg-center bg-no-repeat overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(to right, rgba(10, 25, 47, 0.9), rgba(10, 25, 47, 0.6)), url('https://readdy.ai/api/search-image?query=luxury%20hotel%20lobby%20with%20elegant%20interior%20design%2C%20marble%20floors%2C%20crystal%20chandeliers%2C%20warm%20ambient%20lighting&width=1920&height=600&seq=hotel-premium&orientation=landscape')`
        }}
      >
        <div className="absolute inset-0 bg-navy-900/40"></div>
        <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-4 animate-fade-in-up">
          <h1 className="text-4xl md:text-6xl font-bold mb-4 drop-shadow-lg tracking-tight">
            Find Your Perfect <span className="text-logo-gold-400">Stay</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 mb-8 font-light drop-shadow-md">
            Discover luxury accommodations worldwide.
          </p>
        </div>
      </section>

      {/* Glassmorphism Search Section */}
      <section className="relative z-20 -mt-16 mb-16 mx-4">
        <div className="bg-white/80 backdrop-blur-xl border border-white/40 rounded-3xl shadow-2xl p-6 md:p-8 max-w-6xl mx-auto">
          <form onSubmit={handleSearch} className="space-y-6">
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="relative md:col-span-2" ref={destRef}>
                <i className="ri-map-pin-line absolute left-4 top-1/2 transform -translate-y-1/2 text-logo-gold-500 text-xl z-10"></i>
                <input 
                  placeholder="Where are you going?" 
                  value={destinationQuery} 
                  onChange={(e) => {
                    setDestinationQuery(e.target.value);
                    setSelectedCityId(''); // reset ID when typing
                  }} 
                  className="w-full bg-white text-navy-900 pl-12 pr-4 py-4 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-logo-gold-400 shadow-sm transition-all relative z-0" 
                  required 
                />
                
                {showDestinations && destinations.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 max-h-60 overflow-y-auto z-50">
                    {destinations.map((dest, idx) => (
                      <div 
                        key={idx} 
                        onClick={() => handleSelectDestination(dest)}
                        className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-0 flex items-center"
                      >
                        <i className="ri-building-4-line text-gray-400 mr-3"></i>
                        <span className="text-navy-900 font-medium">{dest.Name || dest.name || 'Unknown City'}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="relative">
                <i className="ri-calendar-line absolute left-4 top-1/2 transform -translate-y-1/2 text-logo-gold-500 text-xl"></i>
                <input type="date" placeholder="Check-in" value={checkIn} onChange={(e) => setCheckIn(e.target.value)} className="w-full bg-white text-navy-900 pl-12 pr-4 py-4 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-logo-gold-400 shadow-sm transition-all" required />
              </div>
              
              <div className="relative">
                <i className="ri-calendar-line absolute left-4 top-1/2 transform -translate-y-1/2 text-logo-gold-500 text-xl"></i>
                <input type="date" placeholder="Check-out" value={checkOut} onChange={(e) => setCheckOut(e.target.value)} className="w-full bg-white text-navy-900 pl-12 pr-4 py-4 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-logo-gold-400 shadow-sm transition-all" required />
              </div>
            </div>

            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div className="relative w-full md:w-auto">
                <button type="button" onClick={() => setShowGuests(!showGuests)} className="w-full md:w-64 text-left bg-white text-navy-900 px-4 py-4 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-logo-gold-400 shadow-sm transition-all flex items-center justify-between">
                  <span className="flex items-center">
                    <i className="ri-user-line text-logo-gold-500 text-xl mr-3"></i>
                    {guests.adults} Adult{guests.adults > 1 ? 's' : ''}, {guests.rooms} Room{guests.rooms > 1 ? 's' : ''}
                  </span>
                  <i className="ri-arrow-down-s-line text-gray-400"></i>
                </button>
                {showGuests && (
                  <div className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 p-4 z-50 w-full md:w-64 animate-fade-in">
                    <div className="flex items-center justify-between mb-4">
                      <span className="font-semibold text-navy-900">Adults</span>
                      <div className="flex items-center space-x-3">
                        <button type="button" onClick={() => setGuests(p => ({ ...p, adults: Math.max(1, p.adults - 1) }))} className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-navy-900 font-bold">-</button>
                        <span>{guests.adults}</span>
                        <button type="button" onClick={() => setGuests(p => ({ ...p, adults: p.adults + 1 }))} className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-navy-900 font-bold">+</button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mb-4">
                      <span className="font-semibold text-navy-900">Rooms</span>
                      <div className="flex items-center space-x-3">
                        <button type="button" onClick={() => setGuests(p => ({ ...p, rooms: Math.max(1, p.rooms - 1) }))} className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-navy-900 font-bold">-</button>
                        <span>{guests.rooms}</span>
                        <button type="button" onClick={() => setGuests(p => ({ ...p, rooms: p.rooms + 1 }))} className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-navy-900 font-bold">+</button>
                      </div>
                    </div>
                    <button type="button" onClick={() => setShowGuests(false)} className="w-full bg-navy-900 text-white py-2 rounded-lg hover:bg-navy-800 transition-colors">Done</button>
                  </div>
                )}
              </div>
            
              <button type="submit" disabled={loading} className="w-full md:w-auto bg-gradient-to-r from-logo-gold-500 to-logo-gold-600 hover:from-logo-gold-400 hover:to-logo-gold-500 text-navy-900 font-bold text-lg px-12 py-4 rounded-xl shadow-lg transition-transform transform hover:scale-105 disabled:opacity-70 disabled:hover:scale-100 flex items-center justify-center">
                {loading ? <i className="ri-loader-4-line animate-spin text-2xl mr-2"></i> : <i className="ri-search-line mr-2"></i>}
                {loading ? 'Searching...' : 'Find Hotels'}
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Search Results */}
      <section className="flex-grow max-w-7xl mx-auto w-full px-4 mb-20">
        {hasSearched && !loading && (
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-navy-900 mb-6">
              {searchResults.length > 0 ? `Found ${searchResults.length} Hotels` : 'No Hotels Found'}
            </h2>
            
            {error && (
               <div className="bg-red-50 text-red-600 p-4 rounded-xl border border-red-200 mb-6">{error}</div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {searchResults.map((hotel, index) => (
                <div key={index} className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 group animate-fade-in-up flex flex-col" style={{ animationDelay: `${index * 0.1}s` }}>
                  
                  {/* Hotel Image */}
                  <div className="relative h-56 overflow-hidden">
                    <img 
                      src={(hotel.images && hotel.images[0]) || hotel.image || 'https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80'} 
                      alt={hotel.name} 
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-lg shadow-sm flex items-center">
                      <i className="ri-star-fill text-logo-gold-500 mr-1 text-sm"></i>
                      <span className="font-bold text-navy-900 text-sm">{hotel.stars || hotel.rating || 4.5}</span>
                    </div>
                  </div>

                  {/* Hotel Details */}
                  <div className="p-6 flex-grow flex flex-col">
                    <h3 className="font-bold text-navy-900 text-xl mb-2 line-clamp-1">{hotel.name}</h3>
                    <p className="text-sm text-gray-500 mb-4 flex items-start">
                      <i className="ri-map-pin-line mr-1 mt-0.5 text-logo-gold-500"></i>
                      <span className="line-clamp-1">{hotel.address || hotel.location || hotel.city || destinationQuery}</span>
                    </p>
                    
                    {/* Amenities (mocked if not from LetSFG) */}
                    <div className="flex flex-wrap gap-2 mb-6">
                      {['Free WiFi', 'Breakfast', 'Pool'].map((amenity, i) => (
                        <span key={i} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-md">{amenity}</span>
                      ))}
                    </div>

                    <div className="mt-auto pt-4 border-t border-gray-100 flex items-end justify-between">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Total Price</p>
                        <p className="text-2xl font-extrabold text-navy-900">
                          ₦{(hotel.retailPrice || hotel.price || 0).toLocaleString()}
                        </p>
                      </div>
                      <button className="bg-navy-900 hover:bg-navy-800 text-white font-bold py-2 px-6 rounded-lg transition-colors shadow-md">
                        View
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}
